import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPen } from 'react-icons/fa';
import { Badge, Button, LabelChip, SortHeader } from '../shared/ui';
import { PopupContext } from '../App';
import FamilyEditorModal from '../components/directory/FamilyEditorModal';
import { API_URL } from '../config/env';
import { listLabels } from '../services/labels';
import { fetchFamilyById, fetchStudentsForFamily } from '../services/studentDetailService';
import dirStyles from './DirectoryPage.module.css';
import styles from './FamilyDetailPage.module.css';

function authHeaders(extra = {}) {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, ...extra };
}

function formatAddress(fam) {
  if (!fam) return '—';
  const line = [fam.address_line1, fam.address_line2].filter(Boolean).join(' ');
  const cityLine = [fam.city, fam.state].filter(Boolean).join(', ');
  const zip = fam.zip_code ? ` ${fam.zip_code}` : '';
  const result = [line, `${cityLine}${zip}`.trim()].filter(Boolean).join(' • ');
  return result || '—';
}

export default function FamilyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const flash = useContext(PopupContext);
  const [family, setFamily] = useState(null);
  const [students, setStudents] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [studentSort, setStudentSort] = useState('');
  const [studentDir, setStudentDir] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setStudentSort('');
    setStudentDir('');
    try {
      const famId = parseInt(id, 10);
      if (Number.isNaN(famId)) throw new Error('Invalid family');
      const [f, studs, labRes] = await Promise.all([
        fetchFamilyById(famId),
        fetchStudentsForFamily(famId),
        fetch(`${API_URL}/api/labels?include_counts=1`, { headers: authHeaders() }).then((r) =>
          r.ok ? r.json() : []
        )
      ]);
      setFamily(f);
      setStudents(studs);
      setLabels(Array.isArray(labRes) ? labRes : []);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setFamily(null);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const onStudentSort = useCallback(({ sort, dir }) => {
    setStudentSort(sort);
    setStudentDir(dir);
  }, []);

  const sortedStudents = useMemo(() => {
    if (!studentSort) return students;
    const dirMult = studentDir === 'desc' ? -1 : 1;
    const copy = [...students];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (studentSort) {
        case 'last_name': {
          const lnA = (a.last_name || a.family?.last_name || '').toLowerCase();
          const lnB = (b.last_name || b.family?.last_name || '').toLowerCase();
          const fnA = (a.first_name || '').toLowerCase();
          const fnB = (b.first_name || '').toLowerCase();
          cmp = lnA.localeCompare(lnB) || fnA.localeCompare(fnB);
          break;
        }
        case 'student_id': {
          const sa = a.student_id != null && a.student_id !== '' ? String(a.student_id) : '';
          const sb = b.student_id != null && b.student_id !== '' ? String(b.student_id) : '';
          cmp = sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
          break;
        }
        case 'dob': {
          const da = a.dob ? new Date(a.dob).getTime() : 0;
          const db = b.dob ? new Date(b.dob).getTime() : 0;
          const na = Number.isNaN(da) ? 0 : da;
          const nb = Number.isNaN(db) ? 0 : db;
          cmp = na - nb;
          break;
        }
        case 'is_active':
          cmp = Number(!!a.is_active) - Number(!!b.is_active);
          break;
        default:
          break;
      }
      if (cmp === 0) {
        cmp = Number(a.id) - Number(b.id);
      }
      return dirMult * (cmp > 0 ? 1 : cmp < 0 ? -1 : 0);
    });
    return copy;
  }, [students, studentSort, studentDir]);

  const refreshLabelsOnly = useCallback(async () => {
    try {
      const labRes = await listLabels();
      if (Array.isArray(labRes)) {
        setLabels(
          labRes.map((l) => ({
            ...l,
            family_count: Number(l.family_count ?? 0) || 0,
            student_count: Number(l.student_count ?? 0) || 0
          }))
        );
      }
    } catch {
      /* non-fatal */
    }
  }, []);

  const reactivateFamily = async () => {
    if (!family) return;
    setReactivating(true);
    try {
      const res = await fetch(`${API_URL}/api/families/${family.id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          first_name: family.first_name,
          last_name: family.last_name,
          phone_number: family.phone_number ?? '',
          email: family.email ?? '',
          address_line1: family.address_line1 ?? '',
          address_line2: family.address_line2 ?? '',
          city: family.city ?? '',
          state: family.state ?? '',
          zip_code: family.zip_code ?? '',
          is_active: true
        })
      });
      if (!res.ok) throw new Error('Failed to reactivate');
      flash('Family reactivated');
      await load();
    } catch (e) {
      flash(e.message || 'Failed to reactivate', 'error');
    } finally {
      setReactivating(false);
    }
  };

  if (loading && !family) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading family…</div>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className={styles.page}>
        <button type="button" className={styles.backBtn} onClick={() => navigate('/families')}>
          <FaArrowLeft aria-hidden /> Back to families
        </button>
        <div className={styles.errorBox}>{error || 'Family not found'}</div>
      </div>
    );
  }

  const displayName = `${family.first_name || ''} ${family.last_name || ''}`.trim();

  return (
    <div className={styles.page}>
      <div className={styles.backRow}>
        <button type="button" className={styles.backBtn} onClick={() => navigate('/families')}>
          <FaArrowLeft aria-hidden /> Families
        </button>
      </div>

      {!family.is_active ? (
        <div className={styles.inactiveBanner} role="status">
          <div className={styles.inactiveBannerText}>
            <strong>This family is inactive.</strong>
            <span>
              The household is hidden from the default directory. Students may still be inactive until you
              activate them individually.
            </span>
          </div>
          <Button type="button" variant="secondary" disabled={reactivating} onClick={reactivateFamily}>
            Reactivate family
          </Button>
        </div>
      ) : null}

      <header className={styles.hero}>
        <div>
          <h1 className={styles.heroTitle}>{displayName || 'Family'}</h1>
          <p className={styles.heroMeta}>
            Household in the directory
            {!family.is_active ? ' · Inactive' : ''}
          </p>
          <span className={styles.caseBadge}>Case #{family.case_number}</span>
          <div className={styles.chipRow}>
            {(family.labels || []).map((l) => (
              <LabelChip key={l.id} label={l.name} color={l.color} />
            ))}
          </div>
        </div>
        <Button type="button" variant="secondary" onClick={() => setEditorOpen(true)}>
          <FaPen aria-hidden /> Edit
        </Button>
      </header>

      <div className={styles.mainGrid}>
        <section className={`${styles.card} ${styles.cardMuted}`} aria-labelledby="fam-contact">
          <h2 id="fam-contact" className={styles.cardTitle}>
            Contact & address
          </h2>
          <p className={styles.cardHint}>How to reach this household</p>
          <dl className={styles.detailGrid}>
            <div className={styles.detailRow}>
              <dt>Email</dt>
              <dd>{family.email || <span className={styles.muted}>—</span>}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Phone</dt>
              <dd>{family.phone_number || <span className={styles.muted}>—</span>}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>Address</dt>
              <dd>{formatAddress(family)}</dd>
            </div>
          </dl>
        </section>

        <section className={styles.card} aria-labelledby="fam-students">
          <h2 id="fam-students" className={styles.cardTitle}>
            Students
          </h2>
          <p className={styles.cardHint}>
            {students.length} {students.length === 1 ? 'student' : 'students'} on file for this case
          </p>
          {students.length === 0 ? (
            <p className={styles.empty}>No students linked to this family.</p>
          ) : (
            <div
              className={styles.tableWrap}
              role="region"
              aria-label="Students in this family"
              tabIndex={0}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      <SortHeader
                        column="last_name"
                        label="Student"
                        currentSort={studentSort}
                        currentDir={studentDir}
                        onSort={onStudentSort}
                      />
                    </th>
                    <th className={styles.thNum}>
                      <SortHeader
                        column="student_id"
                        label="CIN"
                        currentSort={studentSort}
                        currentDir={studentDir}
                        onSort={onStudentSort}
                        align="right"
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="dob"
                        label="DOB"
                        currentSort={studentSort}
                        currentDir={studentDir}
                        onSort={onStudentSort}
                      />
                    </th>
                    <th>
                      <SortHeader
                        column="is_active"
                        label="Status"
                        currentSort={studentSort}
                        currentDir={studentDir}
                        onSort={onStudentSort}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((s) => {
                    const ln = s.last_name || s.family?.last_name || '';
                    const full = `${s.first_name || ''} ${ln}`.trim();
                    return (
                      <tr key={s.id}>
                        <td>
                          <button
                            type="button"
                            className={[dirStyles.entityBtn, styles.studentOpenBtn].join(' ')}
                            onClick={() => navigate(`/students/${s.id}`)}
                            aria-label={`Open student ${full || 'profile'}`}
                          >
                            <span className={dirStyles.entityName}>{full || '—'}</span>
                          </button>
                        </td>
                        <td className={styles.num}>{s.student_id || '—'}</td>
                        <td className={styles.muted}>
                          {s.dob ? new Date(s.dob).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          <Badge variant={s.is_active ? 'success' : 'neutral'}>
                            {s.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <FamilyEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        mode="edit"
        record={family}
        labels={labels}
        onLabelsRefresh={refreshLabelsOnly}
        onSaved={() => void load()}
        flash={flash}
        zIndex={2100}
      />
    </div>
  );
}
